import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbResizeObserver;
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbDimensions } from "abweb/utils/geometry/AbDimensions";
const AB_HIDDEN = Symbol("Hidden");
export class AbResizeObserver {
  constructor(callback) {
    _defineProperty(this, "_callback", void 0);
    _defineProperty(this, "_element", null);
    _defineProperty(this, "_cachedDimensions", undefined);
    _defineProperty(this, "_isHidden", false);
    // Removes this resize observer from the global values
    _defineProperty(this, "unobserveElement", element => {
      const observerArray = AbResizeObserver._globalResizeCallbackMap.get(element);
      if (observerArray === undefined) {
        // SHOULD NOT HAPPEN
        AbResizeObserver._globalResizeObserver.unobserve(element);
        return;
      }
      const index = observerArray.indexOf(this);
      if (index === -1) {
        // SHOULD NOT HAPPEN
        if (observerArray.length === 0) {
          AbResizeObserver._globalResizeCallbackMap.delete(element);
          AbResizeObserver._globalResizeObserver.unobserve(element);
        }
        return;
      }
      if (observerArray.length === 1) {
        // Only unobserve if this is the only observer for this element
        AbResizeObserver._globalResizeCallbackMap.delete(element);
        AbResizeObserver._globalResizeObserver.unobserve(element);
      } else {
        // Someone else
        observerArray.splice(index, 1);
      }
    });
    // Adds this resize observer from the global values
    _defineProperty(this, "observeElement", element => {
      const observerArray = AbResizeObserver._globalResizeCallbackMap.get(element);
      if (observerArray === undefined) {
        AbResizeObserver._globalResizeCallbackMap.set(element, [this]);
        AbResizeObserver._globalResizeObserver.observe(element);
      } else {
        if (observerArray.indexOf(this) === -1) {
          observerArray.push(this);
        }
      }
    });
    _defineProperty(this, "setObservedElement", element => {
      if (element === this._element) return;
      if (this._element) {
        // Clear out old global values
        this.unobserveElement(this._element);
      }
      this._element = element;
      this._cachedDimensions = undefined;
      if (this._element) {
        // Set up new global values
        this.observeElement(this._element);
      }
    });
    /**
     * Stop observing the current observed element.
     */
    _defineProperty(this, "disconnect", () => {
      this.setObservedElement(null);
    });
    /**
     * Observe a new element. Stops observing previously observed element.
     */
    _defineProperty(this, "observe", target => {
      this.setObservedElement(target);
    });
    /**
     * DO NOT USE. This is for calling by the global resize observer only.
     */
    _defineProperty(this, "__dimensionsCallback", dimensions => {
      if (!this._element) return;
      if (dimensions === AB_HIDDEN) {
        if (this._isHidden === true) return;
        this._isHidden = true;
        this._callback(this._cachedDimensions ?? {
          width: 0,
          height: 0
        }, this._element, this._isHidden); // Return size before hidden
        return;
      }
      if (dimensions.height === this._cachedDimensions?.height && dimensions.width === this._cachedDimensions?.width && this._isHidden === false) return; // Don't call if dimensions have not changed
      this._isHidden = false;
      this._cachedDimensions = dimensions;
      this._callback(dimensions, this._element, this._isHidden);
    });
    this._callback = callback;
  }
  set callback(newCallback) {
    this._callback = newCallback;
  }
  get callback() {
    return this._callback;
  }
  get dimensions() {
    return this._cachedDimensions;
  }
  get observedElement() {
    return this._element;
  }
}
_AbResizeObserver = AbResizeObserver;
// Global store mapping observed elements to their observers. Uses WeakMap so detached DOM elements can be GC'd.
_defineProperty(AbResizeObserver, "_globalResizeCallbackMap", new WeakMap());
// Global resize observer to rule them all.
_defineProperty(AbResizeObserver, "_globalResizeObserver", new ResizeObserver(entries => {
  window.requestAnimationFrame(() => {
    entries.forEach(entry => {
      const element = entry.target;
      const observers = _AbResizeObserver._globalResizeCallbackMap.get(element);
      if (observers === undefined) {
        _AbResizeObserver._globalResizeObserver.unobserve(element);
        return;
      }
      const dimensions = element.getClientRects().length === 0 ? AB_HIDDEN : AbDimensions.fromResizeObserverEntry(entry);
      observers.forEach(observer => observer.__dimensionsCallback(dimensions));
    });
  });
}));
//# sourceMappingURL=AbResizeObserver.js.map
